<template>
    <div class="company-user-setup layout-small-form">

        <b-navbar v-if="showBack" class="breadcrumbs">
            <b-navbar-nav>
                <b-nav-item @click="goBack">
                    <b-icon-chevron-left/>
                    Equipment Owner Setup
                </b-nav-item>
            </b-navbar-nav>
        </b-navbar>

        <template v-if="state === ERROR">
            <heading class="text-center" title="Equipment Owner Setup"/>
            <b-card class="justify-content-center frame col-md-7">
                There was an error setting up your account on The Yard. Please contact support.
            </b-card>

        </template>
        <template v-else-if="needsApproval">
            <heading class="text-center" title="Equipment Owner Setup"/>
            <b-card class="justify-content-center frame col-md-7">
                You have successfully requested to join {{ companyUser.companyName }}<br>
                To continue, a company admin at {{ companyUser.companyName }} must approve your account.<br><br>
                <b-button @click="undoCompanyJoin" variant="danger">Join a different company instead</b-button>
            </b-card>

        </template>
        <template v-else-if="state === SELECT_ROLE">
            <heading title="Equipment Owner Setup"/>

            <b-row>
                <b-col cols="12" md="6" class="mb-3 mb-md-0">
                    <b-card class="justify-content-center frame mx-auto">
                        <intro route="companyUserSetup"/>
                        <div class="text-center mb-3">
                            <p>Click below to search for an existing Company to join.</p>
                            <b-button @click="joinCompany">Join a registered Company
                                <b-icon-person/>
                            </b-button>
                        </div>
                    </b-card>
                </b-col>

                <b-col cols="12" md="6">
                    <b-card class="justify-content-center frame mx-auto">
                        <div class="text-center">
                            <p>Click below to create a new Company. If you're not sure if the company already exists
                                <b-button class="btn-text text-blue" @click="joinCompany">click here</b-button>
                                to search for the Company name before proceeding with this step.
                            </p>
                            <b-button @click="newCompany">Create a new Company
                                <b-icon-building/>
                            </b-button>
                        </div>
                    </b-card>
                </b-col>

            </b-row>
        </template>

        <template v-else-if="state === EO_ADMIN">
            <b-alert show variant="warning">
                This page is for creating a new Company in The Yard.<br>If you're not sure if your Company already
                exists on The Yard you can search for the Company name on the
                <button class="btn-text"
                        @click="joinCompany">Join a Company
                </button>
                page.
            </b-alert>
            <data-form title="Company Setup"
                       tag="company-setup"
                       save-button-label="Save"
                       :field-model="companyModel"
                       :field-groups="companyFieldGroups"
                       :load-model-instance="loadCompany"
                       :on-submit-handler="onSubmitCompany"
                       submit-success-message="Company created"
                       submit-error-message="Error creating company"/>
        </template>

        <template v-if="state === EO_USER">
            <b-alert show variant="warning">
                Use the search below to find a Company and send a request to the Admin
                to join. If you don't see your Company in the search then it hasn't been created on The Yard yet.
                Please navigate to the
                <button class="btn-text" @click="newCompany">Create
                    a Company
                </button>
                page to add your Company to The Yard.
            </b-alert>
            <data-form title="Join Company"
                       tag="company-join"
                       save-button-label="Submit"
                       :field-model="companyUserModel"
                       :field-groups="companyUserFieldGroups"
                       :load-model-instance="loadCompanyUser"
                       :on-submit-handler="onSubmitCompanyUser"
                       submit-success-message="Joined company"
                       submit-error-message="Error joining company"/>
        </template>
    </div>
</template>

<script>

import Vue from 'vue'
import Company from '@/models/Company'
import CompanyUser from '@/models/CompanyUser'
import * as _ from 'lodash'

/**
 * Page for EOs to setup companies they have been added to
 */
export default Vue.extend({
    name: 'CompanyUserSetup',
    props: {
        companyUser: Object,
    },
    data() {
        return {

            SELECT_ROLE: 0,
            EO_USER: 1,
            EO_ADMIN: 2,
            ERROR: 3,

            state: 0,

            companyModel: Company,
            companyFieldGroups: [
                ['id', 'name', 'ccb_number', 'alt_license', 'alt_license_type', 'alt_license_number', 'url', 'cobid_certified', 'cobid_number', 'cobid_types'],
                ['mailing_address', 'city', 'state', 'zip'],
            ],

            companyUserModel: CompanyUser,
            companyUserFieldGroups: [
                ['company_id']
            ],
        }
    },
    computed: {
        needsApproval() {
            return this.companyUser && this.companyUser.company_id && !this.companyUser.approved && !this.companyUser.is_company_admin
        },
        showBack() {
            return this.state !== this.SELECT_ROLE && !this.needsApproval
        }
    },
    methods: {
        goBack() {
            this.state = this.SELECT_ROLE
        },
        loadCompany() {
            return Promise.resolve({})
        },
        loadCompanyUser() {
            return Promise.resolve(this.companyUser)
        },
        onSubmitCompany(modelInstance) {
            return this.$api.createCompanyAndAddToCompanyUser(this, this.companyUser.id, modelInstance).then((response) => {
                window.location.reload()
            }).catch(() => {
                this.$toasted.error('Error creating company')
                this.state = this.ERROR
                return Promise.reject()
            })
        },
        onSubmitCompanyUser(modelInstance) {
            return this.$api.joinCompany(this, modelInstance).then((response) => {
                window.location.reload()
            }).catch(() => {
                this.$toasted.error('Error joining company')
                this.state = this.ERROR
                return Promise.reject()
            })
        },
        undoCompanyJoin() {
            let model = _.clone(this.companyUser)
            model['company_id'] = null
            return this.$api.updateItem(this, 'company-user', model).then((response) => {
                window.location.reload()
            }).catch(() => {
                this.$toasted.error('Error joining company')
                this.state = this.ERROR
                return Promise.reject()
            })
        },
        newCompany() {
            //HACK to get the fields to load when switching forms, go back to select_role state first
            this.state = this.SELECT_ROLE
            this.$nextTick(() => {
                this.state = this.EO_ADMIN
            })
        },
        joinCompany() {
            //HACK to get the fields to load when switching forms, go back to select_role state first
            this.state = this.SELECT_ROLE
            this.$nextTick(() => {
                this.state = this.EO_USER
            })
        }
    },
})

</script>

<style lang="scss">
.company-user-setup {
    .alert-warning {
        font-size: 16px;
        line-height: 1.5;
    }

    .card-body {
        border-radius: .5rem;
    }
}
</style>